import React, { useState, useEffect } from 'react';
import { X, Send, Users, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { getUserSubscription } from '../lib/subscription';
import { useTranslation } from '../lib/LanguageContext';
import { NotesMarkdown } from './NotesMarkdown';

interface SendStickyNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  todoId: string;
  todoText: string;
  todoNotes: string | null;
  todoDeadline?: string | null;
}

interface ContactEmail {
  id: string;
  email: string;
}

export function SendStickyNoteModal({
  isOpen,
  onClose,
  todoId,
  todoText,
  todoNotes,
  todoDeadline
}: SendStickyNoteModalProps) {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [contactEmails, setContactEmails] = useState<ContactEmail[]>([]);
  const [isPremium, setIsPremium] = useState(false);
  const [showContactEmails, setShowContactEmails] = useState(false);
  const [keepCopy, setKeepCopy] = useState(false);
  const { user } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen && user) {
      fetchContactEmails();
      checkPremiumStatus();
    }
  }, [isOpen, user]);

  const fetchContactEmails = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('contact_emails')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      setContactEmails(data || []);
    } catch (error) {
      // Silently fail - not critical
    }
  };

  const checkPremiumStatus = async () => {
    try {
      const subscriptionData = await getUserSubscription();
      setIsPremium(subscriptionData?.subscriptionTier === 'premium' || false);
    } catch (error) {
      setIsPremium(false);
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !recipientEmail.trim()) return;
    
    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recipientEmail.trim())) {
      setError(t('enterValidEmail'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // First check if user is premium
      if (!isPremium) {
        setError(t('onlyPremiumCanSend'));
        setLoading(false);
        return;
      }
      
      // Send the sticky note using user's email instead of ID
      const { error: shareError } = await supabase
        .from('shared_todos')
        .insert([{
          sender_email: user.email, // Use only sender_email, remove sender_id
          recipient_email: recipientEmail.trim().toLowerCase(),
          text: todoText,
          notes: todoNotes || '',
          message: message.trim(),
          status: 'pending',
          deadline: todoDeadline || null
        }]);
      
      if (shareError) throw shareError;
      
      // Save the email to contacts if it's not already there
      try {
        const { error: emailError } = await supabase
          .from('contact_emails')
          .upsert([{
            user_id: user.id,
            email: recipientEmail.trim().toLowerCase()
          }], {
            onConflict: 'user_id,email'
          });
      } catch (contactError) {
        // We still continue - this is non-critical
      }
      
      // Delete the original todo only if user doesn't want to keep a copy
      if (!keepCopy) {
        const { error: deleteError } = await supabase
          .from('todos')
          .delete()
          .eq('id', todoId)
          .eq('user_id', user.id);
      }

      setSuccess(true);
      setMessage('');
      fetchContactEmails(); // Refresh contact list
      
      // Reset after 2 seconds and trigger refresh of todos
      setTimeout(() => {
        setSuccess(false);
        window.dispatchEvent(new CustomEvent('refreshTodos')); // Refresh todos to show the deletion
        onClose();
      }, 2000);
    } catch (error) {
      setError(error instanceof Error ? error.message : t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const selectContactEmail = (email: string) => {
    setRecipientEmail(email);
    setShowContactEmails(false);
  };

  const deleteContactEmail = async (id: string) => {
    try {
      const { error } = await supabase
        .from('contact_emails')
        .delete()
        .eq('id', id)
        .eq('user_id', user?.id);
        
      if (error) throw error;
      fetchContactEmails(); // Refresh list
    } catch (error) {
      // Silently fail - not critical
    }
  };

  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center border-b px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('sendStickyNoteTitle')}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {!isPremium ? (
          <div className="px-6 py-4">
            <div className="bg-amber-50 p-4 rounded-md text-amber-800 mb-4">
              <p className="font-medium">{t('premiumFeature')}</p>
              <p className="text-sm mt-1">
                {t('sharingPremiumOnly')}
              </p>
            </div>
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                {t('close')}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSend} className="px-6 py-4">
            <div className="mb-4">
              <div className="font-medium text-gray-800 mb-1">{t('stickyNoteLabel')}</div>
              <div className="bg-indigo-50 p-3 rounded-md text-gray-700">
                {todoText}
              </div>
              {todoNotes && todoNotes.trim() !== '' && (
                <div className="mt-2 text-sm text-gray-500">
                  <div className="font-medium text-gray-700 mb-1">{t('notesLabel')}</div>
                  <NotesMarkdown
                    notes={todoNotes}
                    className="bg-gray-50 p-2 rounded text-gray-600 text-xs max-h-[240px] overflow-y-auto"
                  />
                </div>
              )}
            </div>

            <div className="mb-4 relative">
              <label htmlFor="recipient" className="block text-sm font-medium text-gray-700 mb-1">
                {t('recipientEmailLabel')}
              </label>
              <div className="flex">
                <input
                  type="email"
                  id="recipient"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder={t('emailPlaceholder')}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                  required
                />
                {contactEmails.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowContactEmails(!showContactEmails)}
                    className="ml-2 p-2 bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    title={t('showRecentContacts')}
                  >
                    <Users className="h-5 w-5" />
                  </button>
                )}
              </div>

              {showContactEmails && contactEmails.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                  <div className="p-2 text-xs font-medium text-gray-500 border-b">
                    {t('recentContacts')}
                  </div>
                  <ul>
                    {contactEmails.map(contact => (
                      <li key={contact.id} className="flex justify-between items-center hover:bg-gray-50 border-b last:border-b-0">
                        <button
                          type="button"
                          onClick={() => selectContactEmail(contact.email)}
                          className="px-4 py-2 text-sm text-gray-700 w-full text-left"
                        >
                          {contact.email}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteContactEmail(contact.id);
                          }}
                          className="pr-3 text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="mb-4">
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                {t('messageOptionalLabel')}
              </label>
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('messagePlaceholder')}
                rows={3}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 resize-none"
              />
            </div>

            <div className="mb-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={keepCopy}
                  onChange={(e) => setKeepCopy(e.target.checked)}
                  className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">
                  {t('keepCopyAfterSending')}
                </span>
              </label>
            </div>

            {error && (
              <div className="mb-4 text-sm text-red-600 bg-red-50 p-3 rounded">
                {error}
              </div>
            )}

            {success && (
              <div className="mb-4 text-sm text-green-600 bg-green-50 p-3 rounded">
                {t('stickyNoteSentSuccess')}
              </div>
            )}

            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                {t('cancel')}
              </button>
              <button
                type="submit"
                disabled={loading || !recipientEmail.trim()}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
              >
                {loading ? (
                  <span>{t('sendingButton')}</span>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    {t('send')}
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}